/**
 *  @class
 *  @function FacetsToggle
 */
if (!customElements.get('facets-toggle')) {
  class FacetsToggle extends HTMLElement {
    connectedCallback() {
      this.drawer = document.getElementById('Facet-Drawer');

      this.addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.drawer)) {
          OverlayManager.close(this.drawer);
        } else {
          OverlayManager.open(this.drawer);
        }
      });

      setTimeout(() => {
        window.dispatchEvent(new Event('resize.resize-select'));
      }, 10);
    }
  }
  customElements.define('facets-toggle', FacetsToggle);
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // An entry added here carries its filters. Any other (the one a page
      // was loaded at, in this document or one it replaced, or a #hash) is
      // read from its own address.
      const searchParams = typeof event.state?.searchParams === 'string' ? event.state.searchParams : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        // Back on the entry the page shows (eg. Forward after a Back that
        // couldn't load): its message no longer applies.
        if (searchParams === FacetFiltersForm.searchParamsShown) FacetFiltersForm.showError(false);
        return;
      }
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    // Back from the full load a failed change fell back to, out of the
    // back/forward cache: the results are the ones the message was about.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) FacetFiltersForm.showError(false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // updateURLHash is false for a Back or Forward: the browser has already
  // moved to that entry. Otherwise the entry is added once the results are
  // on the page, so a change that fails adds none.
  static getAvailabilityScopedParams(searchParams) {
    const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
    let hasInventoryScopedFilter = false;

    params.delete('filter.v.availability');

    for (const [key, value] of params.entries()) {
      if (
        key.startsWith('filter.') &&
        key !== 'filter.v.price.gte' &&
        key !== 'filter.v.price.lte' &&
        value
      ) {
        hasInventoryScopedFilter = true;
        break;
      }
    }

    if (hasInventoryScopedFilter) {
      params.set('filter.v.availability', '1');
    }

    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.withView(FacetFiltersForm.getAvailabilityScopedParams(searchParams));
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, searchParams, updateURLHash);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, searchParams, updateURLHash);
      }
    });
  }

  // A ?view= page (an alternate template, eg. a preview) keeps it through
  // filtering: in the results asked for, so their next pages (pagination's
  // data-url) follow it too, and in the history entry.
  static withView(searchParams) {
    const view = new URLSearchParams(window.location.search).get('view');
    if (!view || new URLSearchParams(searchParams).has('view')) return searchParams;
    return `${searchParams ? `${searchParams}&` : ''}view=${encodeURIComponent(view)}`;
  }

  // shopify:collection:update / shopify:search:update — filters or sort changed.
  // Called once the section HTML is in hand so the promise can resolve with the
  // filtered count from #ProductCount (absent when nothing matched → 0).
  static publishFacetUpdate(html) {
    const SE = window.Shopify && window.Shopify.StandardEvents;
    if (!SE) return;
    const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    const countElement = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const resultCount = parseInt(countElement && countElement.dataset.count, 10) || 0;

    if (theme.settings.collection_handle) {
      publishStandardEvent('CollectionUpdateEvent', {
        collection: {
          id: theme.settings.collection_id || null,
          handle: theme.settings.collection_handle,
          productsCount: theme.settings.collection_products_count || 0
        },
        productFilters: SE.CollectionUpdateEvent.parseProductFilters(params),
        sortKey: SE.CollectionUpdateEvent.getSortKey(params),
        promise: Promise.resolve({ productsCount: resultCount })
      });
    } else if (window.location.pathname.indexOf(theme.routes.search_url) !== -1) {
      const query = params.get('q') || new URLSearchParams(window.location.search).get('q') || '';
      if (!query) return;
      publishStandardEvent('SearchUpdateEvent', {
        search: {
          query: query,
          productFilters: SE.SearchUpdateEvent.parseProductFilters(params),
          sortKey: SE.SearchUpdateEvent.getSortKey(params)
        },
        promise: Promise.resolve({ totalCount: resultCount })
      });
    }
  }

  // A newer filter change aborts this request ('facets'). Any other failure
  // shows the message (showError) and sets the filters back to the results
  // on screen. If the server answered with an error, or with a page that
  // has no product grid, the filtered URL then loads in full. If it never
  // answered (offline), the page keeps those results. The URL is left
  // alone: a change only adds its entry once rendered, and rewriting the
  // entry a Back or Forward moved to would turn it into a copy of another.
  static renderSectionFromFetch(url, event, searchParams, updateURLHash) {
    themeFetch(url, {}, 'facets')
      .then(response => response.text())
      .then((html) => {
        if (!new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer')) {
          throw Object.assign(new Error(`No product grid in ${url}`), { status: 200 });
        }
        return html;
      })
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        FacetFiltersForm.renderResults(html, event, searchParams, updateURLHash);
      }, (error) => {
        if (isAbortError(error)) return;
        console.error(error);
        FacetFiltersForm.clearLoading();
        FacetFiltersForm.showError();
        // The filters go back to the results on screen, also before a full
        // load: the browser keeps them with this entry, and Back would
        // otherwise bring the failed choice back over those results.
        const shown = FacetFiltersForm.searchParamsShown;
        FacetFiltersForm.searchParamsPrev = shown;
        FacetFiltersForm.restoreForms(shown);
        if (error.status) {
          const target = new URL(url, window.location.href);
          target.searchParams.delete('section_id');
          // A Back or Forward loads in place of the entry it moved to; a
          // change adds its own, as it would have once rendered.
          if (updateURLHash) {
            window.location.assign(target.href);
          } else {
            window.location.replace(target.href);
          }
        }
      })
      .catch((error) => console.error(error));
  }

  // Shows the results for searchParams and adds their history entry (see
  // renderPage). The entry is added now, with searchParamsShown, not in the
  // view transition's update, which waits for a frame: a Back pressed in
  // that gap would otherwise leave the entry it moved from, and the new
  // entry would then be pushed over the one it moved to.
  static renderResults(html, event, searchParams, updateURLHash) {
    FacetFiltersForm.searchParamsShown = searchParams;
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    withViewTransition(() => {
      FacetFiltersForm.showError(false);
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderSort(searchParams, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      FacetFiltersForm.renderSearchResultsFound(html);
    });
    FacetFiltersForm.publishFacetUpdate(html);
  }

  static clearLoading() {
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
    for (const item of document.getElementsByClassName('thb-filter-count')) {
      item.classList.remove('loading');
    }
    FacetFiltersForm.toggleActiveFacets(false);
  }

  // The message for a filter change that couldn't be shown: above the
  // results and in the filter drawer. Its text is set anew each time so
  // that it is announced again.
  static showError(show = true) {
    document.querySelectorAll('.facets-error').forEach((error) => {
      if (show) {
        const text = error.querySelector('.facets-error__text');
        if (text) text.textContent = window.theme?.strings?.facetsError || text.textContent;
      }
      error.hidden = !show;
    });
    if (!show) return;
    // Out of view above (eg. under the sidebar's filters, which stay in
    // view): brought to it, as the results would have been.
    const pageError = document.querySelector('.collection-container > .facets-error');
    const drawer = document.getElementById('Facet-Drawer');
    if (!pageError || (drawer && OverlayManager.isOpen(drawer))) return;
    const top = pageError.getBoundingClientRect().top - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0) - 30;
    if (top < 0) {
      window.scrollTo({
        top: top + document.documentElement.scrollTop,
        left: 0,
        behavior: theme.reducedMotion ? 'auto' : 'smooth'
      });
    }
  }

  // Sets every facet form's inputs to the filters and sort of searchParams.
  // A sort it doesn't name is the one the page was rendered with.
  static restoreForms(searchParams) {
    const params = new URLSearchParams(searchParams);
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      Array.from(form.elements).forEach((input) => {
        if (!input.name || input.type === 'hidden' || input.closest('price-slider')) return;
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = params.getAll(input.name).includes(input.value);
        } else if (input.tagName === 'SELECT') {
          const rendered = Array.from(input.options).find((option) => option.defaultSelected) || input.options[0];
          input.value = params.has(input.name) ? params.get(input.name) : rendered?.value;
          input.closest('resize-select')?.resizeSelect();
        } else {
          input.value = params.get(input.name) || '';
        }
      });
      // The slider writes its own inputs.
      form.querySelectorAll('.price_slider').forEach((slider) => {
        slider.noUiSlider?.set([params.get(slider.dataset.minName) || 0, params.get(slider.dataset.maxName) || slider.dataset.max]);
      });
    });
  }

  // The sort selects sit outside the rows renderFilters redraws: each is
  // set to the sort of the results, but for the one in the form just
  // changed, which the shopper may be changing still.
  static renderSort(searchParams, event) {
    const changed = event?.target?.closest?.('form');
    const params = new URLSearchParams(searchParams);
    document.querySelectorAll('facet-filters-form select[name="sort_by"]').forEach((select) => {
      if (changed && select.form === changed) return;
      const rendered = Array.from(select.options).find((option) => option.defaultSelected) || select.options[0];
      const value = params.get('sort_by') || rendered?.value;
      if (select.value === value) return;
      select.value = value;
      select.closest('resize-select')?.resizeSelect();
    });
  }

  static renderSectionFromCache(filterDataUrl, event, searchParams, updateURLHash) {
    // The cached result is the newest choice: drop a request still loading.
    themeFetch.abort('facets');
    FacetFiltersForm.renderResults(FacetFiltersForm.filterData.find(filterDataUrl).html, event, searchParams, updateURLHash);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    let top = document.getElementById('ProductGridContainer').getBoundingClientRect().top + document.documentElement.scrollTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: theme.reducedMotion ? "auto" : "smooth"
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  // The search banner's count of results (main-collection-banner), sent
  // along by the search results (main-search). Hidden when there are none:
  // the results say so themselves.
  static renderSearchResultsFound(html) {
    const banners = document.querySelectorAll('[data-search-results-found]');
    if (!banners.length) return;
    const found = new DOMParser().parseFromString(html, 'text/html').getElementById('SearchResultsFound');
    if (!found) return;
    const text = found.content.textContent.trim();
    banners.forEach((banner) => {
      banner.querySelector('p').textContent = text;
      banner.hidden = !text;
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row, #FacetFiltersForm-bar collapsible-row');
    // The mobile drawer, the sidebar and the bar number their rows alike:
    // a row is only ever matched within its own form.
    const formOf = (element) => element.closest('form')?.id;
    const jsFilter = event ? event.target.closest('collapsible-row') : null;
    const matchesIndex = (element) => {
      return jsFilter ? element.dataset.index === jsFilter.dataset.index && formOf(element) === formOf(jsFilter) : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.getElementById(formOf(element))?.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, jsFilter);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      const target = document.querySelector(selector);
      if (!html.querySelector(selector) || !target) return;
      target.innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    // The sidebar's form has no sort of its own: keep the one in use.
    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    if (!form.elements.sort_by && sortBy) searchParams.set('sort_by', sortBy);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// The filters and sort of the results on the page.
FacetFiltersForm.searchParamsShown = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': slider.dataset.minLabel || 'From' },
          { 'aria-label': slider.dataset.maxLabel || 'To' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

(function () {
  const updateShift = (label) => {
    const content = label.querySelector('.facet-tooltip__content');
    if (!content) return;
    label.style.setProperty('--tooltip-shift', '0px');
    const rect = content.getBoundingClientRect();
    const gutter = 8;
    const vw = document.documentElement.clientWidth;
    let shift = 0;
    if (rect.left < gutter) {
      shift = Math.round(gutter - rect.left);
    } else if (rect.right > vw - gutter) {
      shift = Math.round(vw - gutter - rect.right);
    }
    label.style.setProperty('--tooltip-shift', shift + 'px');
  };

  document.addEventListener('mouseover', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label || label._tooltipHovered) return;
    label._tooltipHovered = true;
    updateShift(label);
  });

  document.addEventListener('mouseout', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label) return;
    if (!label.contains(e.relatedTarget)) {
      label._tooltipHovered = false;
    }
  });
})();

const initialAvailabilityScopedParams = FacetFiltersForm.getAvailabilityScopedParams(window.location.search.slice(1));
if (initialAvailabilityScopedParams !== window.location.search.slice(1)) {
  FacetFiltersForm.renderPage(initialAvailabilityScopedParams);
}
